import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["children", "as", "className", "display", "flexDirection", "alignItems", "justifyContent", "backgroundColor", "borderColor", "borderStyle", "borderWidth", "borderRadius", "padding", "paddingBlock", "paddingInline", "position", "height", "width", "UNSAFE_style", "testId"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

/** @jsx jsx */
import { forwardRef } from 'react';
import { css, jsx } from '@emotion/react';
import { SPACING_SCALE } from '../constants';
import { SurfaceContext } from './surface-provider';

/**
 * __Box__
 *
 * Box is a primitive component that has the design decisions of the Atlassian Design System baked in.
 * Renders a `div` by default.
 *
 * @internal
 */
var Box = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
      _ref$as = _ref.as,
      Component = _ref$as === void 0 ? 'div' : _ref$as,
      className = _ref.className,
      _ref$display = _ref.display,
      display = _ref$display === void 0 ? 'flex' : _ref$display,
      flexDirection = _ref.flexDirection,
      alignItems = _ref.alignItems,
      justifyContent = _ref.justifyContent,
      backgroundColorTuple = _ref.backgroundColor,
      borderColorTuple = _ref.borderColor,
      borderStyle = _ref.borderStyle,
      borderWidth = _ref.borderWidth,
      borderRadius = _ref.borderRadius,
      padding = _ref.padding,
      paddingBlock = _ref.paddingBlock,
      paddingInline = _ref.paddingInline,
      _ref$position = _ref.position,
      position = _ref$position === void 0 ? 'relative' : _ref$position,
      height = _ref.height,
      width = _ref.width,
      UNSAFE_style = _ref.UNSAFE_style,
      testId = _ref.testId,
      htmlAttributes = _objectWithoutProperties(_ref, _excluded);

  var _ref2 = backgroundColorTuple || [],
      _ref3 = _slicedToArray(_ref2, 2),
      backgroundColor = _ref3[0],
      backgroundColorFallback = _ref3[1];

  var _ref4 = borderColorTuple || [],
      _ref5 = _slicedToArray(_ref4, 2),
      borderColor = _ref5[0],
      borderColorFallback = _ref5[1];

  var node = jsx(Component, _extends({
    style: _objectSpread(_objectSpread(_objectSpread({}, UNSAFE_style), backgroundColorFallback && {
      '--ds-bg-fb': backgroundColorFallback
    }), borderColorFallback && {
      '--ds-bo-fb': borderColorFallback
    }) // @ts-ignore
    ,
    ref: ref // eslint-disable-next-line @repo/internal/react/no-unsafe-spread-props

  }, htmlAttributes, {
    className: className,
    css: [baseStyles, display && displayMap[display], padding && paddingMap[padding], position && positionMap[position], paddingBlock && paddingBlockMap[paddingBlock], paddingInline && paddingInlineMap[paddingInline], alignItems && flexAlignItemsMap[alignItems], justifyContent && flexJustifyContentMap[justifyContent], backgroundColor && backgroundColorMap[backgroundColor], borderColor && borderColorMap[borderColor], borderStyle && borderStyleMap[borderStyle], borderWidth && borderWidthMap[borderWidth], borderRadius && borderRadiusMap[borderRadius], flexDirection && flexDirectionMap[flexDirection], width && widthMap[width], height && heightMap[height]],
    "data-testid": testId
  }), children);
  return backgroundColor ? jsx(SurfaceContext.Provider, {
    value: backgroundColor
  }, node) : node;
});
Box.displayName = 'Box';
export default Box; // <<< STYLES GO HERE >>>

var borderStyleMap = {
  none: css({
    borderStyle: 'none'
  }),
  solid: css({
    borderStyle: 'solid'
  }),
  dashed: css({
    borderStyle: 'dashed'
  }),
  dotted: css({
    borderStyle: 'dotted'
  })
};
var borderWidthMap = {
  '0px': css({
    borderWidth: '0px'
  }),
  '1px': css({
    borderWidth: '1px'
  }),
  '2px': css({
    borderWidth: '2px'
  }),
  '3px': css({
    borderWidth: '3px'
  })
};
var borderRadiusMap = {
  normal: css({
    borderRadius: '3px'
  }),
  rounded: css({
    borderRadius: '50%'
  }),
  badge: css({
    borderRadius: '8px'
  })
};
var flexDirectionMap = {
  column: css({
    flexDirection: 'column'
  }),
  row: css({
    flexDirection: 'row'
  })
};
var flexAlignItemsMap = {
  center: css({
    alignItems: 'center'
  }),
  baseline: css({
    alignItems: 'baseline'
  }),
  flexStart: css({
    alignItems: 'flex-start'
  }),
  flexEnd: css({
    alignItems: 'flex-end'
  })
};
var flexJustifyContentMap = {
  center: css({
    justifyContent: 'center'
  }),
  flexStart: css({
    justifyContent: 'flex-start'
  }),
  flexEnd: css({
    justifyContent: 'flex-end'
  })
};
var displayMap = {
  block: css({
    display: 'block'
  }),
  inline: css({
    display: 'inline'
  }),
  flex: css({
    display: 'flex'
  }),
  inlineFlex: css({
    display: 'inline-flex'
  })
};
var baseStyles = css({
  boxSizing: 'border-box',
  appearance: 'none',
  border: 'none'
});
var positionMap = {
  absolute: css({
    position: 'absolute'
  }),
  relative: css({
    position: 'relative'
  }),
  static: css({
    position: 'static'
  })
};
/**
 * THIS SECTION WAS CREATED VIA CODEGEN DO NOT MODIFY {@see http://go/af-codegen}
 * @codegen <<SignedSource::57b4c7c177fdfae3f7cd4f00287fd30e>>
 * @codegenId spacing
 * @codegenCommand yarn codegen-styles
 * @codegenParams ["padding", "paddingBlock", "paddingInline", "width", "height"]
 */

var paddingMap = {
  'sp-0': css({
    padding: SPACING_SCALE['sp-0']
  }),
  'sp-25': css({
    padding: SPACING_SCALE['sp-25']
  }),
  'sp-50': css({
    padding: SPACING_SCALE['sp-50']
  }),
  'sp-75': css({
    padding: SPACING_SCALE['sp-75']
  }),
  'sp-100': css({
    padding: SPACING_SCALE['sp-100']
  }),
  'sp-150': css({
    padding: SPACING_SCALE['sp-150']
  }),
  'sp-200': css({
    padding: SPACING_SCALE['sp-200']
  }),
  'sp-300': css({
    padding: SPACING_SCALE['sp-300']
  }),
  'sp-400': css({
    padding: SPACING_SCALE['sp-400']
  }),
  'sp-500': css({
    padding: SPACING_SCALE['sp-500']
  }),
  'sp-600': css({
    padding: SPACING_SCALE['sp-600']
  }),
  'sp-800': css({
    padding: SPACING_SCALE['sp-800']
  })
};
var paddingBlockMap = {
  'sp-0': css({
    paddingBlock: SPACING_SCALE['sp-0']
  }),
  'sp-25': css({
    paddingBlock: SPACING_SCALE['sp-25']
  }),
  'sp-50': css({
    paddingBlock: SPACING_SCALE['sp-50']
  }),
  'sp-75': css({
    paddingBlock: SPACING_SCALE['sp-75']
  }),
  'sp-100': css({
    paddingBlock: SPACING_SCALE['sp-100']
  }),
  'sp-150': css({
    paddingBlock: SPACING_SCALE['sp-150']
  }),
  'sp-200': css({
    paddingBlock: SPACING_SCALE['sp-200']
  }),
  'sp-300': css({
    paddingBlock: SPACING_SCALE['sp-300']
  }),
  'sp-400': css({
    paddingBlock: SPACING_SCALE['sp-400']
  }),
  'sp-500': css({
    paddingBlock: SPACING_SCALE['sp-500']
  }),
  'sp-600': css({
    paddingBlock: SPACING_SCALE['sp-600']
  }),
  'sp-800': css({
    paddingBlock: SPACING_SCALE['sp-800']
  })
};
var paddingInlineMap = {
  'sp-0': css({
    paddingInline: SPACING_SCALE['sp-0']
  }),
  'sp-25': css({
    paddingInline: SPACING_SCALE['sp-25']
  }),
  'sp-50': css({
    paddingInline: SPACING_SCALE['sp-50']
  }),
  'sp-75': css({
    paddingInline: SPACING_SCALE['sp-75']
  }),
  'sp-100': css({
    paddingInline: SPACING_SCALE['sp-100']
  }),
  'sp-150': css({
    paddingInline: SPACING_SCALE['sp-150']
  }),
  'sp-200': css({
    paddingInline: SPACING_SCALE['sp-200']
  }),
  'sp-300': css({
    paddingInline: SPACING_SCALE['sp-300']
  }),
  'sp-400': css({
    paddingInline: SPACING_SCALE['sp-400']
  }),
  'sp-500': css({
    paddingInline: SPACING_SCALE['sp-500']
  }),
  'sp-600': css({
    paddingInline: SPACING_SCALE['sp-600']
  }),
  'sp-800': css({
    paddingInline: SPACING_SCALE['sp-800']
  })
};
var widthMap = {
  'sp-0': css({
    width: SPACING_SCALE['sp-0']
  }),
  'sp-25': css({
    width: SPACING_SCALE['sp-25']
  }),
  'sp-50': css({
    width: SPACING_SCALE['sp-50']
  }),
  'sp-75': css({
    width: SPACING_SCALE['sp-75']
  }),
  'sp-100': css({
    width: SPACING_SCALE['sp-100']
  }),
  'sp-150': css({
    width: SPACING_SCALE['sp-150']
  }),
  'sp-200': css({
    width: SPACING_SCALE['sp-200']
  }),
  'sp-300': css({
    width: SPACING_SCALE['sp-300']
  }),
  'sp-400': css({
    width: SPACING_SCALE['sp-400']
  }),
  'sp-500': css({
    width: SPACING_SCALE['sp-500']
  }),
  'sp-600': css({
    width: SPACING_SCALE['sp-600']
  }),
  'sp-800': css({
    width: SPACING_SCALE['sp-800']
  })
};
var heightMap = {
  'sp-0': css({
    height: SPACING_SCALE['sp-0']
  }),
  'sp-25': css({
    height: SPACING_SCALE['sp-25']
  }),
  'sp-50': css({
    height: SPACING_SCALE['sp-50']
  }),
  'sp-75': css({
    height: SPACING_SCALE['sp-75']
  }),
  'sp-100': css({
    height: SPACING_SCALE['sp-100']
  }),
  'sp-150': css({
    height: SPACING_SCALE['sp-150']
  }),
  'sp-200': css({
    height: SPACING_SCALE['sp-200']
  }),
  'sp-300': css({
    height: SPACING_SCALE['sp-300']
  }),
  'sp-400': css({
    height: SPACING_SCALE['sp-400']
  }),
  'sp-500': css({
    height: SPACING_SCALE['sp-500']
  }),
  'sp-600': css({
    height: SPACING_SCALE['sp-600']
  }),
  'sp-800': css({
    height: SPACING_SCALE['sp-800']
  })
};
/**
 * @codegenEnd
 */

/**
 * THIS SECTION WAS CREATED VIA CODEGEN DO NOT MODIFY {@see http://go/af-codegen}
 * @codegen <<SignedSource::ebb55786a54803214357d0eef0cac448>>
 * @codegenId colors
 * @codegenCommand yarn codegen-styles
 * @codegenParams ["border", "background"]
 */

var borderColorMap = {
  'color.border': css({
    borderColor: "var(--ds-border, var(--ds-bo-fb))"
  }),
  bold: css({
    borderColor: "var(--ds-border-bold, var(--ds-bo-fb))"
  }),
  inverse: css({
    borderColor: "var(--ds-border-inverse, var(--ds-bo-fb))"
  }),
  focused: css({
    borderColor: "var(--ds-border-focused, var(--ds-bo-fb))"
  }),
  input: css({
    borderColor: "var(--ds-border-input, var(--ds-bo-fb))"
  }),
  disabled: css({
    borderColor: "var(--ds-border-disabled, var(--ds-bo-fb))"
  }),
  brand: css({
    borderColor: "var(--ds-border-brand, var(--ds-bo-fb))"
  }),
  selected: css({
    borderColor: "var(--ds-border-selected, var(--ds-bo-fb))"
  }),
  danger: css({
    borderColor: "var(--ds-border-danger, var(--ds-bo-fb))"
  }),
  warning: css({
    borderColor: "var(--ds-border-warning, var(--ds-bo-fb))"
  }),
  success: css({
    borderColor: "var(--ds-border-success, var(--ds-bo-fb))"
  }),
  discovery: css({
    borderColor: "var(--ds-border-discovery, var(--ds-bo-fb))"
  }),
  information: css({
    borderColor: "var(--ds-border-information, var(--ds-bo-fb))"
  })
};
var backgroundColorMap = {
  disabled: css({
    backgroundColor: "var(--ds-background-disabled, var(--ds-bg-fb))"
  }),
  'inverse.subtle': css({
    backgroundColor: "var(--ds-background-inverse-subtle, var(--ds-bg-fb))"
  }),
  input: css({
    backgroundColor: "var(--ds-background-input, var(--ds-bg-fb))"
  }),
  neutral: css({
    backgroundColor: "var(--ds-background-neutral, var(--ds-bg-fb))"
  }),
  'neutral.subtle': css({
    backgroundColor: "var(--ds-background-neutral-subtle, var(--ds-bg-fb))"
  }),
  'neutral.bold': css({
    backgroundColor: "var(--ds-background-neutral-bold, var(--ds-bg-fb))"
  }),
  'brand.bold': css({
    backgroundColor: "var(--ds-background-brand-bold, var(--ds-bg-fb))"
  }),
  selected: css({
    backgroundColor: "var(--ds-background-selected, var(--ds-bg-fb))"
  }),
  'selected.bold': css({
    backgroundColor: "var(--ds-background-selected-bold, var(--ds-bg-fb))"
  }),
  danger: css({
    backgroundColor: "var(--ds-background-danger, var(--ds-bg-fb))"
  }),
  'danger.bold': css({
    backgroundColor: "var(--ds-background-danger-bold, var(--ds-bg-fb))"
  }),
  warning: css({
    backgroundColor: "var(--ds-background-warning, var(--ds-bg-fb))"
  }),
  'warning.bold': css({
    backgroundColor: "var(--ds-background-warning-bold, var(--ds-bg-fb))"
  }),
  success: css({
    backgroundColor: "var(--ds-background-success, var(--ds-bg-fb))"
  }),
  'success.bold': css({
    backgroundColor: "var(--ds-background-success-bold, var(--ds-bg-fb))"
  }),
  discovery: css({
    backgroundColor: "var(--ds-background-discovery, var(--ds-bg-fb))"
  }),
  'discovery.bold': css({
    backgroundColor: "var(--ds-background-discovery-bold, var(--ds-bg-fb))"
  }),
  information: css({
    backgroundColor: "var(--ds-background-information, var(--ds-bg-fb))"
  }),
  'information.bold': css({
    backgroundColor: "var(--ds-background-information-bold, var(--ds-bg-fb))"
  }),
  'color.blanket': css({
    backgroundColor: "var(--ds-blanket, var(--ds-bg-fb))"
  }),
  'color.blanket.selected': css({
    backgroundColor: "var(--ds-blanket-selected, var(--ds-bg-fb))"
  }),
  'color.blanket.danger': css({
    backgroundColor: "var(--ds-blanket-danger, var(--ds-bg-fb))"
  }),
  'elevation.surface': css({
    backgroundColor: "var(--ds-surface, var(--ds-bg-fb))"
  }),
  'elevation.surface.sunken': css({
    backgroundColor: "var(--ds-surface-sunken, var(--ds-bg-fb))"
  }),
  'elevation.surface.raised': css({
    backgroundColor: "var(--ds-surface-raised, var(--ds-bg-fb))"
  }),
  'elevation.surface.overlay': css({
    backgroundColor: "var(--ds-surface-overlay, var(--ds-bg-fb))"
  })
};
/**
 * @codegenEnd
 */